<script setup lang="ts">
import { computed } from 'vue';
import type { IPassword } from '@/components/Interfaces/IPassword';
import InputTextBase from '@/components/Basic/InputTextBase.vue';
import { InputPassword } from '@/components';

const props = withDefaults(
    defineProps<{
        /**
         * The value of the input
         */
        modelValue: IPassword;

        /**
         * The minimum length of the password
         * @default 6
         */
        minimumLength?: number;
    }>(),
    {
        minimumLength: 6,
    },
);

const emits = defineEmits<{
    (e: 'update:modelValue', modelValue: IPassword): void;
    (e: 'passwordMismatch'): void;
    (e: 'passwordMinimumLength', status: boolean): void;
}>();

const value = computed({
    get: () => props.modelValue,
    set: (value: IPassword) => emits('update:modelValue', value),
});

/**
 * Calculate the strength of the password based on the following criteria:
 * - Mixed case
 * - Numbers
 * - Special characters
 * - Length
 */
const passwordStrength = computed(() => {
    // Initialize variables
    let strength = 0;

    let password = value.value.Password || '';

    // Check for mixed case
    if (password.match(/[a-z]/) && password.match(/[A-Z]/)) {
        strength += 1;
    }

    // Check for numbers
    if (password.match(/\d/)) {
        strength += 1;
    }

    // Check for special characters
    if (password.match(/[^a-zA-Z\d]/)) {
        strength += 1;
    }

    // Check password length
    if (password.length < 6) {
        //strength = 0;
    } else {
        strength += 1;
    }
    return (strength / 4) * 100;
});
</script>
<template>
    <div class="form-control-inputpassword d-flex flex-column gap-3">
        <InputPassword
            label="general.newpassword"
            v-model="value.Password!"
            @blur="emits('passwordMinimumLength', value.Password!.length >= props.minimumLength)"
        />
        <div
            class="progress"
            role="progressbar"
            :aria-valuenow="passwordStrength"
            aria-valuemin="0"
            aria-valuemax="100"
        >
            <div
                class="progress-bar"
                :class="{
                    'bg-danger': passwordStrength <= 25,
                    'bg-warning': passwordStrength > 25 && passwordStrength <= 50,
                    'bg-info': passwordStrength > 50 && passwordStrength <= 75,
                    'bg-success': passwordStrength > 75,
                }"
                :style="{ width: `${passwordStrength}%` }"
            ></div>
        </div>
        <div>
            <label class="form-label textlabel">{{ $tm('general.confirmpassword') }}</label>
            <InputTextBase
                v-model="value.ConfirmPassword!"
                type="password"
                @blur="value.ConfirmPassword != value.Password ? emits('passwordMismatch') : null"
            />
        </div>
    </div>
</template>
